import React from 'react';
import { Perspective } from '@feature/event-detail/types';

interface Props {
  perspective: Perspective;
  onTap: () => void;
}

export function PerspectiveCard({ perspective, onTap }: Props): React.ReactElement {
  const viewerCountFormatted =
    perspective.viewerCount >= 1000
      ? `${(perspective.viewerCount / 1000).toFixed(1)}k`
      : perspective.viewerCount.toString();

  const isLive = perspective.streamStatus === 'live';
  const isEnded = perspective.streamStatus === 'ended';
  const hasVod = Boolean(perspective.m3u8Url);
  const isProcessing = isEnded && !hasVod;

  return (
    <div style={styles.card} onClick={onTap}>
      {perspective.thumbnailUrl ? (
        <img
          src={perspective.thumbnailUrl}
          style={styles.thumbnail}
          alt={`${perspective.username}'s perspective`}
        />
      ) : (
        <div style={styles.centerIcon}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="#666">
            <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
          </svg>
        </div>
      )}

      {hasVod && (
        <div style={styles.playIconOverlay}>
          <svg width="56" height="56" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.9)">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
          </svg>
        </div>
      )}

      {isProcessing && (
        <div style={styles.processingOverlay}>
          <span style={styles.processingText}>Processing...</span>
        </div>
      )}

      {isLive && (
        <div style={styles.viewerBadge}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff">
            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
          </svg>
          <span style={styles.viewerBadgeText}>{viewerCountFormatted}</span>
        </div>
      )}

      {isLive && (
        <div style={styles.liveBadge}>
          <div style={styles.liveDot} />
          <span style={styles.liveText}>LIVE</span>
        </div>
      )}

      {hasVod && (
        <div style={styles.vodBadge}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
          </svg>
          <span style={styles.vodText}>RECORDED</span>
        </div>
      )}

      <div style={styles.usernameOverlay}>
        <span style={styles.username}>{perspective.username}</span>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    position: 'relative',
    backgroundColor: '#000',
    borderRadius: '12px',
    overflow: 'hidden',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  thumbnail: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  centerIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  playIconOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  processingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
  },
  processingText: {
    color: 'white',
    fontSize: '12px',
    fontWeight: '600',
  },
  viewerBadge: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: '4px',
    padding: '3px 6px',
  },
  viewerBadgeText: {
    color: '#fff',
    fontSize: '11px',
    fontWeight: '600',
  },
  liveBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#ef4444',
    borderRadius: '4px',
    padding: '3px 6px',
  },
  liveDot: {
    width: '6px',
    height: '6px',
    borderRadius: '3px',
    backgroundColor: '#fff',
  },
  liveText: {
    color: '#fff',
    fontSize: '10px',
    fontWeight: 'bold',
  },
  vodBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#6366f1',
    borderRadius: '4px',
    padding: '3px 6px',
  },
  vodText: {
    color: '#fff',
    fontSize: '10px',
    fontWeight: 'bold',
  },
  usernameOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    padding: '6px 8px',
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
  },
  username: {
    color: '#fff',
    fontSize: '13px',
    fontWeight: '600',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};
